import { useState, useEffect, useCallback, Fragment } from "react";

import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, CircularProgress, Tooltip, IconButton, Box, Modal } from "@mui/material";
import { Mail, TableChartOutlined, Close as CloseIcon } from '@mui/icons-material';

import { ModalSendEmailGroup } from "./ModalSendEmailGroup";
import { exportStandardExcel, } from '../include';

const { origin } = window.location;

const style = {
  overflow: 'hidden',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  maxWidth: '90vw',
  minWidth: '70vw',
  minHeight: '60vh',
  maxHeight: '90vh',
  bgcolor: 'background.paper',
  borderRadius: '1rem',
  boxShadow: 24,
  p: 4,
  display: 'flex',
  flexDirection: 'column',
  padding: '1rem',
  position: "absolute"
};

const mailColumns = [
  { label: 'ID',                key: 'doc_id' },
  { label: 'Fornecedor',        key: 'fornecedor' },
  { label: 'Tipo Documento',    key: 'tipoDocumento' },
  { label: 'Data Aprox.',       key: 'data' },
  { label: 'Embarcação',        key: 'embarcacao' },
  { label: 'Pendências',        key: 'pendencia' },
  { label: 'P.R.',              key: 'pedidoReparo' },
  { label: 'O.M.',              key: 'ordem' },
  { label: 'Aprovação',         key: 'aprovacao' },
  { label: 'Documento',         key: 'documento' }
];

export const TimelineCardTodosDocumentos = (props) => {
  // --- Estados ---
  const [tableData, setTableData] = useState([]);
  const [count, setCount] = useState(0);
  const [showModalSendEmail, setShowModalSendEmail] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // --- Busca de Dados (componentDidMount) ---
  useEffect(() => {
    if (!props.showModal) return;

    const loadData = async () => {
      const events = props.fatos.length > 0
        ? props.fatos
            .map(valoresOcorrencias => valoresOcorrencias.ocorrencias)
            .reduce((ocorrencias, values) => ocorrencias.concat(values, []), [])
        : [];

      if (events.length === 0) return;

      setIsLoading(true);

      const newFatos = props.fatos
        .filter((object, index) => index === props.fatos.findIndex(value => object.fato_id === value.fato_id))
        .filter(item => item.ocorrencias.length !== 0);

      const queryParams = {
        filters: JSON.stringify(newFatos),
        textFilter: props.textFilter,
        dataInicio: props.dataInicio,
        dataFim: props.dataFim,
        showUnread: props.showUnread,
        showPend: props.showPend,
        showEdt: props.showEdt,
      };

      const queryStringParams = new URLSearchParams(queryParams);
      try {
        const { data } = await props.api.get(`timeline/todos-documentos?${queryStringParams.toString()}`);

        const tableDataSanitized = data.map(curr => ({
          doc_id: curr.document_id,
          name: curr.boat,
          tipoDocumento: curr.tipo,
          fornecedor: curr.fornecedor,
          aprovacao: curr.aprov,
          pedidoReparo: curr.pr,
          ordem: curr.om,
          link: `/mediateca?alertdoc=${curr.document_id}`,
          pendencia: curr.pendencias,
          data: curr.dtaprox
        }));

        setTableData(tableDataSanitized);
        setCount(tableDataSanitized.length);
      } catch (error) {
        console.error("Erro ao carregar documentos da timeline:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, [props.showModal, props.fatos, props.api, props.textFilter, props.dataInicio, props.dataFim, props.showUnread, props.showPend, props.showEdt]);

  // --- Handlers ---
  const onPressRow = (id) => {
    props.onSelectRow(id);
    // props.onCloseCard(); // fecha o modal todos documentos
  };

const exportToExcel = useCallback(() => {
    if (count === 0) return;
    setIsExporting(true);

    try {
      // 1. Preparação dos Filtros
      const parts = [];
      if (props.showUnread) parts.push('Não lidos');
      if (props.showPend) parts.push('Com pendências');
      if (props.showEdt) parts.push('Editados');

      let dateRange = '';
      if (props.dataInicio) dateRange = `De ${props.dataInicio} `;
      if (props.dataFim) dateRange += `Até ${props.dataFim}`;
      if (dateRange) parts.push(dateRange);

      // 2. Mapeamento de Dados
      const data = tableData.map(fls => ([
        fls.doc_id,
        fls.fornecedor || 'xxx',
        fls.tipoDocumento,
        new Intl.DateTimeFormat('pt-BR').format(new Date(fls.data)) || 'Data inválida',
        fls.name || 'xxx',
        fls.pendencia,
        fls.pedidoReparo,
        fls.ordem,
        fls.aprovacao,
        { t: 's', v: ' Abrir Documento', l: { Target: `${origin}/viewer.html?p=${fls.doc_id}` } }
      ]));

      // 3. Chamada da Função Genérica
      exportStandardExcel({
        title: `Linha do Tempo: ${count === 1 ? '1 Documento' : `${new Intl.NumberFormat('pt-BR').format(count)} Documentos`}`,
        filtersText: parts.length ? parts.join(', ') : '',
        columns: [
          { header: 'ID', width: 6 },
          { header: 'Fornecedor', width: 20 },
          { header: 'Tipo do Documento', width: 22 },
          { header: 'Data Aproximada', width: 15 },
          { header: 'Embarcação', width: 15 },
          { header: 'Pendências', width: 20 },
          { header: 'P.R.', width: 10 },
          { header: 'O.M.', width: 10 },
          { header: 'Aprovação', width: 10 },
          { header: 'Ação', width: 25 }
        ],
        data: data,
        fileNamePrefix: 'linha do tempo'
      });

    } finally {
      setIsExporting(false);
    }
  }, [count, tableData, props.showUnread, props.showPend, props.showEdt, props.dataInicio, props.dataFim]);


  const onExportClick = () => {
    // Simula o processamento assíncrono para garantir o feedback visual do loader
    setTimeout(() => exportToExcel(), 100);
  };

  const dateFormatter = new Intl.DateTimeFormat('pt-BR');

  return (
    <>
      <Modal
        open={props.showModal}
        onClose={props.onCloseCard}
        disableEscapeKeyDown={isExporting}
      >
        <Box sx={style}>
          <header style={{ paddingBottom: "6px", borderBottom: "1px solid gray", display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            
            <h2 style={{ padding: 0, margin: 0, color: "#3f3f3f" }}>
              {`Lista de ${count === 1 ? '1 Documento' : `${new Intl.NumberFormat('pt-BR').format(count)} Documentos`}`}
            </h2>
            
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip title="Exportar para Excel" arrow>
                <IconButton size="large" sx={{ color: '#106436ff' }} onClick={onExportClick} disabled={isExporting}>
                  <TableChartOutlined />
                </IconButton>
              </Tooltip>

              <Tooltip title="Enviar lista de documentos por email" arrow>
                <IconButton onClick={() => setShowModalSendEmail(true)}>
                  <Mail />
                </IconButton>
              </Tooltip>

              <Tooltip title="Fechar" arrow>
                <IconButton onClick={props.onCloseCard}><CloseIcon /></IconButton>
              </Tooltip>
            </div>
            
          </header>

          <div style={{ position: 'relative', overflowY: 'hidden', height: 'calc(100% - 64px)' }}>
            {isExporting && (
              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10, backgroundColor: 'rgba(255,255,255,0.6)' }}>
                <CircularProgress size={128} thickness={6} />
              </Box>
            )}

            <TableContainer sx={{ maxHeight: 480 }}>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Seq</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Fornecedor</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>Data</TableCell>
                    <TableCell>Embarcação</TableCell>
                    <TableCell>Pendência ou Falha</TableCell>
                    <TableCell>P.R.</TableCell>
                    <TableCell>O.M.</TableCell>
                    <TableCell>Aprovação</TableCell>
                    <TableCell>Documento</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {isLoading && (
                    <TableRow><TableCell align="center" colSpan={10}><CircularProgress /></TableCell></TableRow>
                  )}
                  {tableData.map((item, index) => (
                    <TableRow 
                      key={item.doc_id} 
                      hover // Adicionado feedback visual do Material UI
                      style={{ cursor: 'pointer' }}
                      onClick={() => {
                        // PREVENÇÃO DE CLIQUE ACIDENTAL AO SELECIONAR TEXTO
                        const selection = window.getSelection().toString();
                        if (selection.length > 0) return;
                        
                        onPressRow(item.doc_id);
                      }} 
                    >
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{item.doc_id}</TableCell>
                      <TableCell>{item.fornecedor || 'xxx'}</TableCell>
                      <TableCell>{item.tipoDocumento || 'xxx'}</TableCell>
                      <TableCell>{item.data ? dateFormatter.format(new Date(item.data)) : 'xxx'}</TableCell>
                      <TableCell>{item.name || 'xxx'}</TableCell>
                      <TableCell>
                        {item.pendencia ? item.pendencia.split('|').map((text, idx) => (
                          <Fragment key={idx}>{text}<br /></Fragment>
                        )) : 'xxx'}
                      </TableCell>
                      <TableCell>{item.pedidoReparo || 'xxx'}</TableCell>
                      <TableCell>{item.ordem || 'xxx'}</TableCell>
                      <TableCell>{item.aprovacao || 'xxx'}</TableCell>
                      <TableCell>
                        <a style={{ color: '#0000EE' }} href={`viewer.html?p=${item.doc_id}`} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()}>
                          {`${origin}/viewer.html?p=${item.doc_id}`}
                        </a>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </div>
        </Box>
      </Modal>

      {showModalSendEmail && (
        <ModalSendEmailGroup
          api={props.api}
          show={showModalSendEmail}
          onClose={() => setShowModalSendEmail(false)}
          data={tableData}
          type="list-documents"
          functionFormatData={formatDataToReportMail}
          orderKeys={mailColumns}
          showAlert={props.alert}
        />
      )}
    </>
  );
};

function formatDataToReportMail(tableData = []) {
  if (!Array.isArray(tableData)) return [];
  
  return tableData.map(curr => {
    const fdata = curr.data ? new Intl.DateTimeFormat('pt-BR').format(new Date(curr.data)) : null;
    return {
      doc_id: curr.doc_id,
      embarcacao: curr.name || null,
      tipoDocumento: curr.tipoDocumento || null,
      fornecedor: curr.fornecedor || null,
      aprovacao: curr.aprovacao || null,
      pedidoReparo: curr.pedidoReparo || null,
      ordem: curr.ordem || null,
      documento: `${origin}/viewer.html?p=${curr.doc_id}`,
      pendencia: curr.pendencia || null,
      data: fdata
    };
  });
}